/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Cloud, Moon, ArrowRight, TrendingUp, Flame, Calendar } from 'lucide-react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';

/**
 * Building blocks of the DailyBriefingCard (Sprint 9D).
 * Extracted from DailyBriefingCard.jsx (Code Quality refactor).
 */

const GREETINGS = [
  { until: 12, icon: Sun, key: 'greetingMorning', fallback: 'Buongiorno', tone: 'from-amber-500/15 via-rose-400/10 to-primary/10' },
  { until: 18, icon: Cloud, key: 'greetingAfternoon', fallback: 'Buon pomeriggio', tone: 'from-primary/15 via-sky-400/10 to-emerald-400/10' },
  { until: 25, icon: Moon, key: 'greetingEvening', fallback: 'Buonasera', tone: 'from-indigo-500/15 via-purple-500/10 to-primary/10' },
];

/** Time-aware greeting (icon, label with first name, gradient tone). */
export const greetingForHour = (h, t, name) => {
  const short = (name || '').split(' ')[0] || '';
  const g = GREETINGS.find((x) => h < x.until) || GREETINGS[GREETINGS.length - 1];
  return { icon: g.icon, label: t(g.key, { defaultValue: g.fallback }) + (short ? `, ${short}` : ''), tone: g.tone };
};

/** Fetches /ai/daily-briefing with optional forced regeneration. */
export const useDailyBriefing = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true); else setLoading(true);
      const r = await api.get('/ai/daily-briefing', { params: force ? { force: true } : {} });
      setData(r.data);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('[DailyBriefing] load failed', e);
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  return { data, loading, refreshing, refresh: () => load(true) };
};

export const BriefingSkeleton = () => (
  <Card className="p-4 rounded-2xl bg-gradient-to-br from-primary/10 via-card to-card border-primary/30 animate-pulse" data-testid="daily-briefing-loading">
    <div className="h-4 w-2/3 bg-secondary rounded mb-2" />
    <div className="h-3 w-full bg-secondary/60 rounded mb-1" />
    <div className="h-3 w-4/5 bg-secondary/60 rounded" />
  </Card>
);

/** Builds the stat tiles from the briefing highlights (only non-zero). */
export const buildBriefingStats = (highlights, t) => {
  const h = highlights || {};
  return [
    { key: 'trending_posts_count', label: t('trendingPosts'), value: h.trending_posts_count || 0, icon: Flame, to: '/feed' },
    { key: 'new_initiatives_count', label: t('latestInitiatives'), value: h.new_initiatives_count || 0, icon: TrendingUp, to: '/bacheca' },
    { key: 'upcoming_shifts_count', label: t('upcoming'), value: h.upcoming_shifts_count || 0, icon: Calendar, to: '/shifts' },
  ].filter((s) => s.value > 0);
};

/** Animated grid of deep-link stat tiles. */
export const BriefingStats = ({ stats }) => {
  const navigate = useNavigate();
  return (
    <AnimatePresence>
      {stats.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2"
        >
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.button
                key={s.key}
                onClick={() => navigate(s.to)}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.07, type: 'spring', stiffness: 180, damping: 18 }}
                whileHover={{ y: -2, transition: { duration: 0.15 } }}
                whileTap={{ scale: 0.97 }}
                className="group flex items-center gap-2 rounded-2xl bg-background/50 hover:bg-background/80 border border-border/60 p-2.5 text-left transition-colors"
                data-testid={`briefing-stat-${s.key}`}
              >
                <div className="h-8 w-8 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-lg font-bold leading-none">{s.value}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{s.label}</div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.button>
            );
          })}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
